import React, { useEffect, useMemo, useState } from 'react';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { Button, Card, Table } from 'react-bootstrap';
import { toTitleCase } from '../../utilities/TextFormatters.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { LoadingSpinner } from '../../Loading.tsx';
import { useRepToolContext } from '../../../RepToolContext.tsx';

interface ReptileSpecificStat {
  count: number,
  reptile: string,
  value: string,
}

interface ReptileStat {
  count: number,
  value: number,
}

interface ReptileValue {
  max_price: number,
  min_price: number,
  average_price: number,
  total: number,
  value: string,
}

interface CollectionStats {
  number_of_reptiles: ReptileStat[],
  morphs: ReptileSpecificStat[],
  sexes: ReptileSpecificStat[],
  collection_value: ReptileValue[],
}


const StatItem = ({ children, title }: { children: React.ReactNode, title: string }) => (
  <div>
    <h4>{title}</h4>
    <Card className='bg-dark text-light'>
      <Card.Body>
        <Card.Text>
          {children}
        </Card.Text>
      </Card.Body>
    </Card>
  </div>

)


export const CollectionStatsModal = ({
  closeModal,
}: {
  closeModal: () => void,
}) => {
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [showAllMorphs, setShowAllMorphs] = useState(false);
  const [showAllSexes, setShowAllSexes] = useState(false);
  const [showAllReptiles, setShowAllReptiles] = useState(false);
  const [fetching, setFetching] = useState(true);
  const { appendControls } = useRepToolContext(); 
  const fetchStats = () => {
    setFetching(true);
    BasicFetch({
      url: `${(window as any).serverLocation}/user/stats`,
      onSuccess: ({ stats }: { stats: CollectionStats }) => {
        setStats(stats);
      },
      onFinish: () => setFetching(false)
    })
  }

  useEffect(() => {
    appendControls(null);
    fetchStats();
  }, []);

  const displayMorphs = useMemo(() => {
    if (!stats) return [];
    if (showAllMorphs) return stats.morphs;
    return stats.morphs.slice(0, 5);
  }, [stats, showAllMorphs]);

  const displaySexes = useMemo(() => {
    if (!stats) return [];
    if (showAllSexes) return stats.sexes;
    return stats.sexes.slice(0, 5);
  }, [stats, showAllSexes]);

  const displayReptiles = useMemo(() => {
    if (!stats) return [];
    if (showAllReptiles) return stats.number_of_reptiles;
    return stats.number_of_reptiles.slice(0, 5);
  }, [stats, showAllReptiles]);

  const collectionValue = useMemo(() => {
    if (!stats) return 0;
    // add up all the average prices
    return stats.collection_value.reduce((acc, val) => acc + val.average_price, 0);
  }, [stats]);

  const reptileCount = useMemo(() => {
    if (!stats) return 0;
    return stats.number_of_reptiles.reduce((acc, val) => acc + val.count, 0);
  }, [stats]);

  const morphCount = useMemo(() => {
    if (!stats) return 0;
    return stats.morphs.reduce((acc, val) => acc + val.count, 0);
  }, [stats]);

  return (
    <ReptoolModal id="change-morph-dialog" title={'Collection Stats'} closeModal={closeModal} body={(
      <>
        {stats && <Button onClick={fetchStats} disabled={fetching} variant='outline-light'>
          {fetching ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Refresh'}
        </Button>}
        <div className='mt-3'>

          {!stats ? <div className='m-auto' style={{
            width: '200px',
          }}><LoadingSpinner /></div> : <>
            {!!collectionValue && <StatItem title="Collection Value">
              <h6>Total: ${collectionValue.toFixed(2)}</h6>
              <Table striped bordered hover variant="dark" size='sm'>
                <thead>
                  <tr>
                    <th>Max Price</th>
                    <th>Min Price</th>
                    <th>Average Price</th>
                    <th>Morph</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    stats.collection_value.map((stat) => (
                      <tr>
                        <td>${stat.max_price.toFixed(2)}</td>
                        <td>${stat.min_price.toFixed(2)}</td>
                        <td>${stat.average_price.toFixed(2)}</td>
                        <td>{stat.value}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </Table>
            </StatItem>}
            <div id="stats-grid" className='mt-3'>
              <StatItem title='Number of Reptiles'>
                <h6>Total: {reptileCount}</h6>
                <Table striped bordered hover variant="dark" size='sm'>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Reptile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      displayReptiles.map((stat) => (
                        <tr>
                          <td className='text-right'>{stat.count}</td>
                          <td>{stat.value}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </Table>
                {
                  stats.number_of_reptiles.length > 5 && <Button size='sm' variant='outline-light' className='mt-2' onClick={() => setShowAllReptiles(!showAllReptiles)}>{showAllReptiles ? 'Show Less' : 'Show All'}</Button>
                }

              </StatItem>
              <StatItem title='Morphs'>
                <h6>Total: {morphCount}</h6>
                <Table striped bordered hover variant="dark" size='sm'>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Morph Name</th>
                      <th>Reptile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      displayMorphs.map((stat) => (
                        <tr>
                          <td className='text-right'>{stat.count}</td>
                          <td>{stat.value}</td>
                          <td>{stat.reptile}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </Table>
                {
                  stats.morphs.length > 5 && <Button size='sm' variant='outline-light' className='mt-2' onClick={() => setShowAllMorphs(!showAllMorphs)}>{showAllMorphs ? 'Show Less' : 'Show All'}</Button>
                }
              </StatItem>
              <StatItem title='Sexes'>
                <Table striped bordered hover variant="dark" size='sm'>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Sex</th>
                      <th>Reptile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      displaySexes.map((stat) => (
                        <tr>
                          <td className='text-right'>{stat.count}</td>
                          <td>{toTitleCase(stat.value)}</td>
                          <td>{stat.reptile}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </Table>
                {
                  stats.sexes.length > 5 && <Button size='sm' variant='outline-light' className='mt-2' onClick={() => setShowAllSexes(!showAllSexes)}>{showAllSexes ? 'Show Less' : 'Show All'}</Button>
                }
              </StatItem>
            </div>
          </>}
        </div>
      </>
    )} />
  )
}